// Полоса записи голоса (FR-22..FR-25). Вставляется между логом и вводом.
//
// Всё состояние записи — своё у каждой панели: MediaRecorder, AudioContext,
// таймер и интервал волны живут в этом компоненте и в срезе сессии.
// Никаких общих синглтонов: две панели пишут одновременно и не мешают друг другу.

import { useCallback, useEffect, useRef } from 'react'
import { acquireMic } from '../lib/micBusy'
import { sendVoice } from '../lib/voiceUpload'
import { useLogStore, usePane, VOICE_BARS } from '../store/logStore'
import './composer.css'

/** Обновление эквалайзера — раз в 260 мс, как в дизайне. */
const WAVE_MS = 260
const BAR_MIN = 3
const BAR_MAX = 18
/** RMS выше — в микрофоне есть звук (≈ −48 dBFS); ниже — тишина */
const SOUND_RMS = 0.004
/** столько подряд без звука — предупреждаем, пока человек ещё говорит */
const SILENCE_WARN_MS = 4000

interface Rec {
  recorder: MediaRecorder
  media: MediaStream
  ctx: AudioContext | null
  analyser: AnalyserNode | null
  chunks: Blob[]
  startedAt: number
  tick: ReturnType<typeof setInterval>
  wave: ReturnType<typeof setInterval>
  /** отпустить «микрофон занят» (lib/micBusy); повторный вызов безвреден */
  mic: () => void
}

interface Props {
  sessionId: string
  /** доставка расшифровки клиентом — только если сервер старый и не знает
   *  autosend; обычно текст агенту отправляет сам сервер */
  onTranscript(text: string, seconds: number, voiceId?: string | null): void
}

function mmss(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Понятная причина отказа микрофона вместо пустой полосы (FR-25). */
function micError(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? ''
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'доступ к микрофону запрещён — разрешите его в настройках браузера для этого сайта'
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'микрофон не найден — подключите устройство записи'
  }
  if (name === 'NotReadableError') {
    return 'микрофон занят другой программой'
  }
  const msg = (err as { message?: string } | null)?.message
  return msg ? `не удалось начать запись: ${msg}` : 'не удалось начать запись'
}

export default function VoiceBar({ sessionId, onTranscript }: Props) {
  const phase = usePane(sessionId, (s) => s.voice.phase)
  const seconds = usePane(sessionId, (s) => s.voice.seconds)
  const bars = usePane(sessionId, (s) => s.voice.bars)
  const error = usePane(sessionId, (s) => s.voice.error)
  const warn = usePane(sessionId, (s) => s.voice.warn)
  const device = usePane(sessionId, (s) => s.voice.device)
  const patchVoice = useLogStore((s) => s.patchVoice)
  const resetVoice = useLogStore((s) => s.resetVoice)

  const recRef = useRef<Rec | null>(null)
  const barsRef = useRef<number[]>(new Array(VOICE_BARS).fill(BAR_MIN))
  const startedRef = useRef(false)

  /** Погасить железо: таймеры, дорожки, аудиоконтекст. */
  const teardown = useCallback((): Rec | null => {
    const rec = recRef.current
    recRef.current = null
    if (!rec) return null
    clearInterval(rec.tick)
    clearInterval(rec.wave)
    // запись кончилась (отправка, отмена, сбой старта) — оркестратор может слушать
    rec.mic()
    return rec
  }, [])

  const release = useCallback((rec: Rec) => {
    rec.media.getTracks().forEach((t) => t.stop())
    if (rec.ctx) void rec.ctx.close().catch(() => undefined)
  }, [])

  /** Завершить запись: send=false — отмена, в лог и на сервер ничего (FR-24). */
  const finish = useCallback((send: boolean) => {
    const rec = teardown()
    if (!rec) {
      resetVoice(sessionId)
      return
    }
    const durationSec = Math.max(1, Math.round((Date.now() - rec.startedAt) / 1000))
    const done = () => {
      release(rec)
      if (!send) {
        resetVoice(sessionId)
        return
      }
      const blob = new Blob(rec.chunks, { type: rec.recorder.mimeType || 'audio/webm' })
      if (blob.size === 0) {
        patchVoice(sessionId, { phase: 'error', error: 'запись пустая — микрофон ничего не дал' })
        return
      }
      // Дальше — без этой панели: загрузку держит модуль, расшифровку и
      // отправку агенту делает сервер. Полоса закрывается сразу, в беседе
      // висит «расшифровываю…» (VoicePending), уходить из беседы можно.
      resetVoice(sessionId)
      sendVoice(sessionId, blob, durationSec, onTranscript)
    }
    if (send) patchVoice(sessionId, { phase: 'sending' })
    if (rec.recorder.state !== 'inactive') {
      rec.recorder.onstop = done
      try {
        rec.recorder.stop()
      } catch {
        done()
      }
    } else {
      done()
    }
  }, [teardown, release, resetVoice, patchVoice, onTranscript, sessionId])

  // старт записи — ровно один раз на фазу «requesting»
  useEffect(() => {
    if (phase !== 'requesting' || startedRef.current) return
    startedRef.current = true
    let cancelled = false

    const fail = (message: string) => {
      patchVoice(sessionId, { phase: 'error', error: message })
    }

    const run = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        fail('браузер не даёт доступ к микрофону — нужен https или localhost')
        return
      }
      if (typeof MediaRecorder === 'undefined') {
        fail('браузер не умеет запись звука (MediaRecorder недоступен)')
        return
      }
      // «Микрофон занят» — уже на запросе доступа: голосовой оркестратор глохнет
      // до первого слова. Отпускаем на каждом выходе до создания Rec, дальше — teardown.
      const mic = acquireMic()
      let media: MediaStream
      try {
        media = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (err) {
        mic()
        fail(micError(err))
        return
      }
      if (cancelled) {
        mic()
        media.getTracks().forEach((t) => t.stop())
        return
      }

      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(media)
      } catch (err) {
        mic()
        media.getTracks().forEach((t) => t.stop())
        fail(micError(err))
        return
      }

      // анализатор уровня — честная волна, а не рандом
      let ctx: AudioContext | null = null
      let analyser: AnalyserNode | null = null
      const AC: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (AC) {
        try {
          ctx = new AC()
          analyser = ctx.createAnalyser()
          analyser.fftSize = 512
          ctx.createMediaStreamSource(media).connect(analyser)
          // без жеста пользователя контекст стартует suspended и анализатор врёт нулями
          void ctx.resume().catch(() => undefined)
        } catch {
          ctx = null
          analyser = null
        }
      }

      const chunks: Blob[] = []
      recorder.ondataavailable = (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data)
      }

      const startedAt = Date.now()
      barsRef.current = new Array(VOICE_BARS).fill(BAR_MIN)

      // Следим за самим микрофоном: пока идёт запись, он может замолчать (смена
      // устройства, bluetooth-гарнитура, системный мьют) — а запись «идёт» и
      // отправится тишиной. Поэтому предупреждаем сразу, а не после отправки.
      const track = media.getAudioTracks()[0]
      let lastSoundAt = startedAt
      let trackDead: string | null = null
      if (track) {
        track.onmute = () => { trackDead = 'микрофон отключён системой' }
        track.onunmute = () => { trackDead = null; lastSoundAt = Date.now() }
        track.onended = () => { trackDead = 'микрофон отключился — остановите запись' }
      }

      const tick = setInterval(() => {
        patchVoice(sessionId, { seconds: Math.floor((Date.now() - startedAt) / 1000) })
      }, 1000)

      const wave = setInterval(() => {
        let level = BAR_MIN
        if (analyser) {
          const data = new Uint8Array(analyser.fftSize)
          analyser.getByteTimeDomainData(data)
          let sum = 0
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128
            sum += v * v
          }
          const rms = Math.sqrt(sum / data.length)
          level = Math.min(BAR_MAX, BAR_MIN + rms * 70)
          if (rms > SOUND_RMS) lastSoundAt = Date.now()
        }
        const next = barsRef.current.slice(1)
        next.push(Math.round(level))
        barsRef.current = next
        // «молчит» судим по анализатору только когда контекст реально работает
        const measuring = ctx !== null && ctx.state === 'running'
        const quiet = measuring && Date.now() - lastSoundAt > SILENCE_WARN_MS
        patchVoice(sessionId, {
          bars: next,
          warn: trackDead ?? (quiet ? 'микрофон молчит — не слышу звука' : null),
        })
      }, WAVE_MS)

      const rec: Rec = { recorder, media, ctx, analyser, chunks, startedAt, tick, wave, mic }
      recRef.current = rec
      try {
        recorder.start(250)
      } catch (err) {
        teardown()
        release(rec)
        fail(micError(err))
        return
      }
      patchVoice(sessionId, {
        phase: 'recording', seconds: 0, error: null, warn: null, device: track?.label ?? '',
      })
    }

    void run()
    return () => { cancelled = true }
  }, [phase, sessionId, patchVoice, teardown, release])

  // размонтирование панели во время записи — молча всё погасить
  useEffect(() => () => {
    const rec = recRef.current
    recRef.current = null
    if (!rec) return
    clearInterval(rec.tick)
    clearInterval(rec.wave)
    rec.mic()
    try {
      if (rec.recorder.state !== 'inactive') {
        rec.recorder.onstop = null
        rec.recorder.stop()
      }
    } catch {
      /* уже остановлен */
    }
    rec.media.getTracks().forEach((t) => t.stop())
    if (rec.ctx) void rec.ctx.close().catch(() => undefined)
  }, [])

  if (phase === 'error') {
    return (
      <div className="ab-voicebar">
        <div className="ab-recdot ab-recdot--still" />
        <div className="ab-recerr">{error ?? 'ошибка записи'}</div>
        <button
          type="button"
          className="ab-recbtn"
          onClick={() => { startedRef.current = false; resetVoice(sessionId) }}
        >
          закрыть
        </button>
      </div>
    )
  }

  const sending = phase === 'sending'
  const label = phase === 'requesting'
    ? 'жду доступ к микрофону'
    : sending ? 'отправляю' : (!sending && warn) ? warn : 'запись голосового'
  const warning = phase === 'recording' && !!warn

  return (
    <div className="ab-voicebar">
      <div className={`ab-recdot${sending ? ' ab-recdot--still' : ''}`} />
      <span className={`ab-rectext${warning ? ' ab-rectext--warn' : ''}`} title={device || undefined}>{label}</span>
      <div className="ab-eq" aria-hidden>
        {bars.map((h, i) => (
          <div className="ab-eq__bar" key={i} style={{ height: `${h}px` }} />
        ))}
      </div>
      <span className="ab-rectimer">{mmss(seconds)}</span>
      <button
        type="button"
        className="ab-recbtn"
        disabled={sending}
        onClick={() => { startedRef.current = false; finish(false) }}
      >
        отмена
      </button>
      <button
        type="button"
        className="ab-recbtn ab-recbtn--send"
        disabled={sending || phase !== 'recording'}
        onClick={() => { startedRef.current = false; finish(true) }}
      >
        отправить
      </button>
    </div>
  )
}
